import {
    faCaretDown,
    faCaretRight,
    faChartSimple,
    faClone,
    faCog,
    faFileLines,
    faPencil,
    faPerson,
    faPersonWalking,
    faPlay,
    faRotate,
    faTag,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import { AuthError, User, multiFactor, sendEmailVerification } from "firebase/auth";
import Loader from "../loader/Loader";
import { getNotificationPreferenceById, updateNotificationPreference } from "../../../services/notificationServices/NotificationService";
import editicons from '../../../assets/images/editicons.svg';
import './settings.css';
import { Button, ButtonGroup } from "react-bootstrap";
import { ROLES } from "../../../Data/SD";
import { toast } from "react-toastify";
import { NotificationPreferences } from "../../../interfaces/UserInfo";
import EmailVerificationPrompt from "./EmailVerificationPrompt";

const Settings: React.FC = () => {
    const { currentUser, currentRoles } = useAuth();
    const userId = (currentUser as any)?.uid;
    const token = (currentUser as any)?.accessToken;

    const [soundNotificationToggle, setSoundNotificationToggle] =
        useState<boolean>(false);
    const [selectedSound, setSelectedSound] = useState(
        localStorage.getItem("notificationSound") || "notification-4.mp3"
    );

    const [loading, setLoading] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const [isPreferenceDropdownOpen, setIsPreferenceDropdownOpen] = useState(false);
    const [preferences, setPreferences] = useState<NotificationPreferences>({
        email: false,
        web: false,
        reminderTime: 0,
    });

    const navigate = useNavigate();

    const navigateToEditRecurringTasks = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/edit/task/recurring");
        } else {
            navigate("/edit/task/recurring");
        }
    };

    const navigateToEditWorkflows = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/edit/workflows");
        } else {
            navigate("/edit/workflows");
        }
    };

    const navigateToEditParticipants = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/edit/participants");
        } else {
            navigate("/edit/participants");
        }
    };

    const navigateToEditTemplateInterimTasks = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/edit/interims");
        } else {
            navigate("/edit/interims");
        }
    };

    const navigateToTemplateFiles = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/edit/templateFiles");
        } else {
            navigate("/edit/templateFiles");
        }
    };

    const navigateToActivityTemplate = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/task/edit/activity/template");

        } else {
            navigate("/task/edit/activity/template");
        }
    };

    const navigateToInsights = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/insights");
        } else {
            navigate("/insights");
        }
    };

    const navigateToTagDefinitions = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/tags");
        } else {
            navigate("/tags");
        }
    };

    const navigateToWorkflowSetting = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/workflow/justification");
        } else {
            navigate("/workflow");
        }
    };

    const navigateToTemplateExclusion = () => {
        if (checkOwnerRole()) {
            navigate("/dashboard/owner/taskexclusion");
        }
    };

    const checkOwnerRole = (): boolean => {
        if (currentRoles?.some((role) => role.name === "OWNER" || role.name === "ADMIN")) {
            return true;
        }
        return false;
    }

    const toggleNotificationSound = () => {
        setSoundNotificationToggle(!soundNotificationToggle);
    };

    // Notification set timing
    const timingOptions = [
        { label: "5 Minutes", value: 5 },
        { label: "15 Minutes", value: 15 },
        { label: "1 Hour", value: 60 },
    ];

    // Fetch current preferences on load
    const preferencesToggleDropdown = () => {
        setIsPreferenceDropdownOpen((prev) => !prev);
    };

    useEffect(() => {
        let isMounted = true;
        const fetchPreferences = async () => {
            setLoading(true);
            const result = await getNotificationPreferenceById(token, userId);
            if (result && isMounted) {
                setPreferences({
                    email: result.email,
                    web: result.web,
                    reminderTime: result.reminderTime || 0,
                });
            }
            setLoading(false);
        };

        if (userId) fetchPreferences();
        return () => {
            isMounted = false;
        };
    }, [userId]);

    const handleToggle = async (type: 'email' | 'web') => {
        const updatedPreferences = {
            ...preferences,
            [type]: !preferences[type],
        };

        setPreferences(updatedPreferences);
        setLoading(true);
        try {
            const success = await updateNotificationPreference(
                token,
                userId,
                updatedPreferences.email,
                updatedPreferences.web,
                updatedPreferences.reminderTime
            );
            if (success) console.log("Preference updated successfully");
        } catch (error) {
            console.error("Error updating preferences");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedPreferences = {
            ...preferences,
            reminderTime: Number(e.target.value),
        };
        setPreferences(updatedPreferences);
        setSelectedValue(e.target.value);
        setLoading(true);
        try {
            const success = await updateNotificationPreference(
                token,
                userId,
                updatedPreferences.email,
                updatedPreferences.web,
                updatedPreferences.reminderTime
            );
            if (success) console.log("Reminder time updated");
        } catch (error) {
            console.error("Error updating reminder time");
        } finally {
            setLoading(false);
        }
    };

    const setNotificationSound = (soundFileName: string) => {
        localStorage.setItem("notificationSound", soundFileName);
        setSelectedSound(soundFileName);
    };

    const playSound = (soundFileName: string) => {
        const audio = new Audio(`/notificationTones/${soundFileName}`);
        audio
            .play()
            .then(() => { })
            .catch((error) => {
                console.error(`Error playing ${soundFileName}:`, error);
            });
    };

    return (
        <>
            <div className="common_card">
                <div className="top top_bar_flx border_tittle">
                    <div className="top_bar_tittle">
                        <h2>Settings</h2>
                    </div>
                    {/* <div className="top_right_flx">
              <Button variant="secondary" className="btn_sm"
                onClick={toggleNotificationSound}
              >
                Notification Sounds
                {soundNotificationToggle ? (
                  <FontAwesomeIcon icon={faCaretDown} />
                ) : (
                  <FontAwesomeIcon icon={faCaretRight} />
                )}

                {soundNotificationToggle && (
                  <div className="notification_sound_list">
                    {[
                      "notification-1.mp3",
                      "notification-2.mp3",
                      "notification-3.mp3",
                      "notification-4.mp3",
                    ].map((sound, index) => (
                      <div
                        key={index}
                        className="notification_sound_flx"
                      >
                        <input
                          type="radio"
                          id={sound}
                          name="notificationSound"
                          value={sound}
                          checked={selectedSound === sound}
                          onChange={() => setNotificationSound(sound)}
                        />
                        <label htmlFor={sound}>{sound}</label>
                        <div className="" onClick={() => playSound(sound)}>
                          <FontAwesomeIcon icon={faPlay}/>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Button>
          </div> */}
                </div>
                {currentRoles?.some(
                    (role) =>
                        role.name === "PROGRAM MANAGER" ||
                        role.name === "OWNER" ||
                        role.name === "ADMIN"
                ) ? (
                    <div className="task_activity_options_grid mby_24">
                        {/* <div className="task_activity_options_card">
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faRotate} /></div>
                                <h3>Recurring Tasks</h3>
                            </div>
                            <button
                                onClick={navigateToEditRecurringTasks}
                                className="relative"
                            >
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div> */}
                        {/* <div className="task_activity_options_card">
          <h3>Workflows</h3>
          <button onClick={navigateToEditWorkflows} className="relative flex items-center text-primary cursor-pointer">
            <img style={{width: '1.5rem'}} src={editicons} alt="editicons" />
          </button>
        </div> */}
                        {/* <div className="task_activity_options_card">
                        
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faPerson} /></div>
                                <h3>Edit Participants</h3>
                            </div>
                            <button onClick={navigateToEditParticipants}>
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div>
                        <div className="task_activity_options_card">
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faFileLines} /></div>
                                <h3>Template Interim Tasks</h3>
                            </div>

                            <button
                                onClick={navigateToEditTemplateInterimTasks}
                                className="relative"
                            >
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div>
                        <div className="task_activity_options_card">
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faClone} /></div>
                                <h3>Template Files</h3>
                            </div>
                            <button
                                onClick={navigateToTemplateFiles}
                                className="relative flex items-center text-primary cursor-pointer"
                            >
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div>
                        <div className="task_activity_options_card">
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faPersonWalking} /></div>
                                <h3>Task Activity Types</h3>
                            </div>
                            <button
                                onClick={navigateToActivityTemplate}
                                className="relative"
                            >
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div>
                        <div className="task_activity_options_card">
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faChartSimple} /></div>
                                <h3>Insights</h3>
                            </div>
                            <button
                                onClick={navigateToInsights}
                                className="relative"
                            >
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div>
                        <div className="task_activity_options_card">
                            <div className="task_activity_options_card_txt">
                                <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faTag} /></div>
                                <h3>Tag Defintions</h3>
                            </div>
                            <button
                                onClick={navigateToTagDefinitions}
                                className="relative"
                            >
                                <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                            </button>
                        </div> */}
                        {/* {currentRoles.some(r => r.name == ROLES.OWNER) &&
                            (
                                <>
                                    <div className="task_activity_options_card">
                                        <div className="task_activity_options_card_txt">
                                            <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faCog} /></div>
                                            <h3>Workflow Setting</h3>
                                        </div>
                                        <button
                                            onClick={navigateToWorkflowSetting}
                                            className="relative"
                                        >
                                            <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                                        </button>
                                    </div>
                                    <div className="task_activity_options_card">
                                        <div className="task_activity_options_card_txt">
                                            <div className="task_activity_options_card_icon"><FontAwesomeIcon icon={faCog} /></div>
                                            <h3>Template Exclusion</h3>
                                        </div>
                                        <button
                                            onClick={navigateToTemplateExclusion}
                                            className="relative"
                                        >
                                            <img style={{ width: '1.5rem' }} src={editicons} alt="editicons" />
                                        </button>
                                    </div>
                                </>

                            )} */}

                    </div>
                ) : (
                    <div className="mt-5 flex">
                        {/* <div className="relative">
                            <Button variant="secondary" className=""
                                onClick={toggleNotificationSound}
                            >
                                Notification Sounds
                                {soundNotificationToggle ? (
                                    <FontAwesomeIcon icon={faCaretDown} />
                                ) : (
                                    <FontAwesomeIcon icon={faCaretRight} />
                                )}
                            </Button>
                            {soundNotificationToggle && (
                                <div className="flex flex-col gap-4 ms-10 border-l-4 border-blue-500 ps-4">
                                    {[
                                        "notification-1.mp3",
                                        "notification-2.mp3",
                                        "notification-3.mp3",
                                        "notification-4.mp3",
                                    ].map((sound, index) => (
                                        <div
                                            key={index}
                                            className="flex align-middle items-center gap-2"
                                        >
                                            <input
                                                type="radio"
                                                id={sound}
                                                name="notificationSound"
                                                value={sound}
                                                checked={selectedSound === sound}
                                                onChange={() => setNotificationSound(sound)}
                                            />
                                            <label htmlFor={sound}>{sound}</label>
                                            <div
                                                className="p-2 bg-blue-500 h-6 w-6 flex justify-center align-middle items-center rounded-full text-white cursor-pointer"
                                                onClick={() => playSound(sound)}
                                            >
                                                <FontAwesomeIcon icon={faPlay} className="text-xs" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div> */}
                    </div>
                )}
                <div className="verify_email_flx">

                    {/* {currentUser && !currentUser.emailVerified && (
                        <EmailVerificationPrompt user={currentUser} />
                    )} */}
                    {/* <div className="">
                        {loading && <Loader />}
                        <Button className="btn_sm" onClick={preferencesToggleDropdown} >
                            Notification Setting
                            <FontAwesomeIcon icon={isPreferenceDropdownOpen ? faCaretDown : faCaretRight} />
                        </Button>

                        {isPreferenceDropdownOpen && (
                            <div className="mt-4 space-y-4">
                                <p className="text-sm font-medium mb-2">Notification Preferences:</p>
                                <div className="flex flex-col gap-3">
                                    <label className="d-flex align-items-center gap-2 w-100">
                                        <input
                                            type="checkbox"
                                            checked={preferences.email}
                                            onChange={() => handleToggle("email")}
                                            disabled={loading}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">Email</span>
                                    </label>

                                </div>

                                <div className="pt-2 border-t">
                                    <p className="text-sm font-medium mb-2">Reminder Timing:</p>
                                    <form className="flex flex-col gap-2">
                                        {timingOptions.map((option) => (
                                            <label key={option.value} className="d-flex align-items-center gap-2 w-100">
                                                <input
                                                    type="radio"
                                                    name="notification-timing"
                                                    value={option.value}
                                                    checked={preferences.reminderTime === option.value}
                                                    onChange={handleChange}
                                                    className="w-4 h-4"
                                                />
                                                <span className="text-sm">{option.label}</span>
                                            </label>
                                        ))}
                                    </form>
                                </div>
                            </div>
                        )}
                    </div> */}
                </div>
                {/* {currentUser &&
                    currentUser.emailVerified &&
                    multiFactor(currentUser)?.enrolledFactors?.length === 0 && (
                        <div className="mt-6 border-t pt-6">
                            <div className="top border_tittle">
                                <div className="top_bar_tittle">
                                    <h2>Multi-Factor Authentication</h2>
                                </div>
                            </div>
                            <p className="mt-1 text-sm text-gray-600">
                                Enhance your account security by enabling two-factor
                                authentication.
                            </p>
                            <button
                                onClick={() => navigate("/mfa-setup")}
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Set Up 2FA
                            </button>
                        </div>
                    )} */}
            </div>
        </>
    );
};

export default Settings;
